import { BpTabs } from '../../../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../../../shared/components/utility/tabs/tab/BpTab'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { AdvertiserReportFilterInterface } from '../../model/types/AdvertiserReportFilter.interface'
import { selectAdvertiserReportFilters } from '../../model/paidResultsSlice'
import {
  AdvertiserReportItem,
  ExportDomainAdvertisersArg,
  useExportDomainAdvertisers,
  useGetNonCompliantAffiliatesQuery,
} from '../../../../app/api'
import { LatestAdsTab } from './tabs/LatestAdsTab'
import { AffiliatesTab } from './tabs/AffiliatesTab'
import { AdvertisersTab } from './tabs/AdvertisersTab'
import { useTranslation } from 'react-i18next'
import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import React from 'react'

interface PaidResultsTableExpandRowProps extends AdvertiserReportItem {
  selectedTab: 'latest_ads' | 'affiliates' | 'advertisers'
  onTabChange?: (tab: string) => void
}

export const PaidResultsTableExpandRow = ({
  ads,
  shopping_ads,
  advertiser,
  advertisers,
  selectedTab = 'latest_ads',
  onTabChange,
}: PaidResultsTableExpandRowProps) => {
  const { t } = useTranslation()
  const {
    project,
    dateRange: date,
    country,
    policy,
    keyword,
    affiliate,
    advertiserName,
    show_hidden,
    show_shopping_ads,
    engine,
    browser,
    reversed_filters,
  } = useAppSelector<AdvertiserReportFilterInterface>(selectAdvertiserReportFilters)

  // в reversed_filters отдаём только то, что понимает конкретная ручка
  const pickReversed = (keys: string[]) =>
    reversed_filters
      ?.split(',')
      .filter((key) => keys.includes(key))
      .join(',') || undefined

  const { data: affiliates, isLoading } = useGetNonCompliantAffiliatesQuery(
    {
      projectId: +project,
      countryId: country || undefined,
      engineId: engine || undefined,
      browserId: browser || undefined,
      policy,
      date,
      site: advertiser,
      keywordId: keyword || undefined,
      affiliateId: affiliate || undefined,
      advertiserId: advertiserName || undefined,
      isHidden: show_hidden,
      showShoppingAds: show_shopping_ads,
      // is_decloak,
      // tag_ids: tags?.length ? tags.split(',') : undefined,
      reversedFilters: reversed_filters || undefined,
      'per-page': 100,
    },
    { skip: !project || !advertiser },
  )
  const affiliateItems = affiliates?.data?.items ?? []
  const affiliateTotalItems = affiliates?.data?.total ?? 0

  const [exportDomainAdvertisers, { isFetching: exportDomainAdvertisersLoading }] =
    useExportDomainAdvertisers()

  if (isLoading) {
    return (
      <div className={'flex justify-center py-[16px]'}>
        <div className={'relative w-[30px]'}>
          <Preloader />
        </div>
      </div>
    )
  }

  return (
    <div className={'rounded-[12px] bg-white shadow-depth-200 shadow-focus-500/[0.24]'}>
      <BpTabs active={selectedTab} className={'pl-[16px]'} size={'md'} onToggle={onTabChange}>
        <BpTab
          activeKey={'latest_ads'}
          counter={ads?.length}
          title={t('pages.paidResults.lastAds')}
        >
          <LatestAdsTab ads={ads} advertiser={advertiser} shoppingAds={shopping_ads} />
        </BpTab>
        <BpTab
          activeKey={'affiliates'}
          counter={affiliateItems.length}
          title={t('pages.paidResults.affiliates')}
        >
          <AffiliatesTab
            advertiser={advertiser}
            affiliates={affiliateItems}
            total={affiliateTotalItems}
          />
        </BpTab>
        <BpTab
          panel={
            <div className={'px-[16px]'}>
              <ExportDrop
                columns={[
                  { keys: ['advertiser'], label: t('table.columns.advertiser') },
                  { keys: ['affiliate_id'], label: t('table.columns.affiliateId') },
                  { keys: ['ads_count'], label: t('table.columns.ads') },
                  { keys: ['search_engine'], label: t('table.columns.searchEngine') },
                  { keys: ['countries'], label: t('table.columns.countries') },
                  { keys: ['last_found'], label: t('table.columns.lastFound') },
                ]}
                loading={exportDomainAdvertisersLoading}
                onExport={(exportParams) =>
                  exportDomainAdvertisers({
                    ...exportParams,
                    projectId: Number(project),
                    site: advertiser,
                    date,
                    countryId: country || undefined,
                    engineId: engine || undefined,
                    browserId: browser || undefined,
                    policy: policy as ExportDomainAdvertisersArg['policy'],
                    keywordId: keyword || undefined,
                    affiliateId: affiliate || undefined,
                    advertiserId: advertiserName || undefined,
                    reversedFilters: pickReversed([
                      'country_id',
                      'engine_id',
                      'browser_id',
                      'keyword_id',
                      'affiliate_id',
                      'advertiser_id',
                    ]),
                    showShoppingAds: show_shopping_ads,
                  })
                }
              />
            </div>
          }
          activeKey={'advertisers'}
          counter={advertisers?.length}
          title={t('pages.paidResults.advertisers')}
        >
          <AdvertisersTab advertisers={advertisers} />
        </BpTab>
      </BpTabs>
    </div>
  )
}
